/**
 * ScriptEmbed — executes a module instance's `custom_script` config on the
 * public page. Rendered once per module by ModularPage, so every existing
 * and future module type gets this for free without per-type code.
 *
 * `<script>` tags inserted via innerHTML never execute — the browser
 * intentionally ignores them. We work around that by walking the parsed
 * fragment and re-creating every element (recreating <script> nodes via
 * createElement so the browser does run them), same trick analytics/chat
 * widget snippets rely on elsewhere on the web.
 */
import { useEffect, useRef } from "react";

function cloneAndExecute(sourceNode, destNode) {
  Array.from(sourceNode.childNodes).forEach((child) => {
    if (child.nodeType === 1 && child.tagName === "SCRIPT") {
      const script = document.createElement("script");
      Array.from(child.attributes).forEach((attr) => script.setAttribute(attr.name, attr.value));
      // createElement("script") defaults async=true for external scripts,
      // which can run an inline init script before a sibling library tag
      // finishes loading. Preserve execution order unless the author
      // explicitly opted into async.
      if (!child.hasAttribute("async")) script.async = false;
      script.textContent = child.textContent;
      destNode.appendChild(script);
    } else if (child.nodeType === 1) {
      const clone = child.cloneNode(false);
      destNode.appendChild(clone);
      cloneAndExecute(child, clone);
    } else {
      destNode.appendChild(child.cloneNode(true));
    }
  });
}

export default function ScriptEmbed({ html }) {
  const containerRef = useRef(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    container.innerHTML = "";
    if (!html) return;
    const template = document.createElement("template");
    template.innerHTML = html;
    cloneAndExecute(template.content, container);
  }, [html]);

  if (!html) return null;
  return <div ref={containerRef} data-testid="module-custom-script" />;
}
